import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, LayoutTemplate, Save } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { Cargando } from '@/shared/ui/Cargando';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Campo, claseEntrada } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';
import { useSesion } from '@/shared/sesion/SesionProvider';
import type { ContactoConCambio, ContactoDelPortal } from './contrato';
import { PestanasDelPortal } from './PestanasDelPortal';

const CLAVE = ['portal', 'contacto'];

export function PaginaPieDePagina() {
  const contacto = useQuery({
    queryKey: CLAVE,
    queryFn: () => api.get<ContactoConCambio>('/portal/contacto'),
  });

  return (
    <>
      <EncabezadoDePagina
        icono={LayoutTemplate}
        color="esmeralda"
        titulo="Portal ciudadano"
        subtitulo="El contacto que se ve en el pie de la ventanilla"
      />

      <PestanasDelPortal />

      {contacto.isPending || !contacto.data ? (
        <Cargando titulo="Cargando el contacto" detalle="Obteniendo la ficha" alto="h-48" />
      ) : (
        <FichaDeContacto key={contacto.data.actualizadoEn ?? 'sin-cambios'} datos={contacto.data} />
      )}
    </>
  );
}

function FichaDeContacto({ datos }: { datos: ContactoConCambio }) {
  const cliente = useQueryClient();
  const { puede } = useSesion();
  const soloLectura = !puede('portal.editar');
  const [ficha, setFicha] = useState<ContactoDelPortal>(datos.contacto);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  function campo<K extends keyof ContactoDelPortal>(clave: K) {
    return {
      value: ficha[clave] ?? '',
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setFicha((f) => ({ ...f, [clave]: e.target.value })),
      className: claseEntrada,
      readOnly: soloLectura,
    };
  }

  async function guardar(evento: React.FormEvent) {
    evento.preventDefault();
    setError(null);
    setGuardando(true);
    try {

      const limpio = Object.fromEntries(
        Object.entries(ficha).map(([k, v]) => [
          k,
          typeof v === 'string' && v.trim() === '' ? null : v,
        ]),
      ) as unknown as ContactoDelPortal;
      await api.put<ContactoConCambio>('/portal/contacto', limpio);
      void cliente.invalidateQueries({ queryKey: CLAVE });
      notificar.exito('Contacto guardado. La ventanilla lo toma en unos minutos.');
    } catch (causa) {
      const mensaje =
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar el contacto';
      setError(mensaje);
      notificar.error(mensaje);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form onSubmit={(e) => void guardar(e)} className="space-y-5">
      <div className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
        <Globe className="mt-0.5 size-4 shrink-0 text-blue-600" aria-hidden />
        <p className="text-xs uppercase leading-relaxed text-blue-800">
          <span className="font-semibold">Lo que se deja en blanco no se enseña.</span> Mejor sin
          horario que con uno equivocado.
          {datos.actualizadoPor && (
            <>
              {' '}
              Último cambio: {datos.actualizadoPor}
              {datos.actualizadoEn
                ? `, ${new Date(datos.actualizadoEn).toLocaleString('es-EC')}`
                : ''}
              .
            </>
          )}
        </p>
      </div>

      {error && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <section className="rounded-2xl border border-armazon-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-armazon-700">
          Contacto
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <Campo etiqueta="Teléfono" ayuda="COMO SE LEE: 03 299 0018 EXT. 5015">
            <input {...campo('telefono')} maxLength={60} />
          </Campo>
          <Campo
            etiqueta="Número para marcar"
            ayuda="SOLO DÍGITOS, CON EL CÓDIGO DEL PAÍS: +59332990018"
          >
            <input {...campo('telefonoMarcable')} maxLength={20} inputMode="tel" />
          </Campo>
          <Campo etiqueta="Correo">
            <input {...campo('correo')} maxLength={150} type="email" />
          </Campo>
          <Campo etiqueta="Horario de atención" ayuda="EN BLANCO HASTA QUE ESTÉ CONFIRMADO">
            <input
              {...campo('horario')}
              maxLength={150}
              placeholder="Lunes a viernes, 08:00 a 17:00"
            />
          </Campo>
          <div className="md:col-span-2">
            <Campo etiqueta="Dirección de las oficinas">
              <input {...campo('direccion')} maxLength={200} />
            </Campo>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-armazon-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-armazon-700">
          Redes sociales y lema
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <Campo etiqueta="Facebook" ayuda="LA DIRECCIÓN COMPLETA, CON https://">
            <input {...campo('facebook')} maxLength={300} />
          </Campo>
          <Campo etiqueta="Instagram">
            <input {...campo('instagram')} maxLength={300} />
          </Campo>
          <Campo etiqueta="TikTok">
            <input {...campo('tiktok')} maxLength={300} />
          </Campo>
          <Campo etiqueta="Lema" ayuda="UNA LÍNEA: «UN CANTÓN MÁS LIMPIO, MÁS SALUDABLE»">
            <input {...campo('lema')} maxLength={150} />
          </Campo>
        </div>
      </section>

      {!soloLectura && (
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={guardando}
            className="inline-flex items-center gap-2 rounded-xl bg-marca-600 px-5 py-2.5 text-sm font-semibold uppercase text-white shadow-sm transition-colors hover:bg-marca-700 disabled:opacity-60"
          >
            <Save className="size-4" aria-hidden />
            {guardando ? 'Guardando' : 'Guardar'}
          </button>
        </div>
      )}
    </form>
  );
}
